import { useEffect, useRef, useState } from "react";
import { streamSchedule, recentVods } from "../../../mocks/gameData";

export default function LiveStreams() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="streams" ref={ref} className="relative py-28 bg-[#07070f] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#e879f9] to-transparent opacity-30" />
      <div className="absolute top-32 right-0 w-80 h-80 rounded-full bg-[#a855f7]/5 blur-3xl" />
      <div className="absolute bottom-16 left-0 w-80 h-80 rounded-full bg-[#22d3ee]/4 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className={`flex flex-col items-center text-center mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-widest mb-3">// catch me live</span>
          <h2 className="font-orbitron text-3xl md:text-5xl font-black uppercase text-white mb-2">
            Live <span className="neon-text-fuchsia">Streams</span>
          </h2>
          <p className="font-rajdhani text-base text-slate-400 max-w-xl">
            Tune in live on Twitch or catch recent VODs below. The schedule is set — no excuses to miss a drop.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Schedule */}
          <div
            className={`lg:col-span-2 transition-all duration-700 delay-100 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="card-gaming rounded-xl p-6 h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-calendar-schedule-line text-[#e879f9] text-xl" />
                </div>
                <h3 className="font-orbitron text-sm font-bold uppercase tracking-wider text-white">
                  Stream Schedule
                </h3>
              </div>
              <div className="space-y-3">
                {streamSchedule.map((s, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-200 ${
                      s.isLive
                        ? "border-[#e879f9]/50 bg-[#e879f9]/5"
                        : "border-white/5 bg-white/2 hover:border-white/10"
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-orbitron text-xs font-bold text-white">{s.day}</p>
                        {s.isLive && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono-tech bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse">
                            LIVE
                          </span>
                        )}
                      </div>
                      <p className="font-mono-tech text-xs text-[#22d3ee] mt-0.5">{s.time}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-rajdhani text-xs text-slate-400">{s.game}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Twitch CTA */}
              <a
                href="#"
                className="flex items-center justify-center gap-2 mt-6 w-full py-3 rounded-md bg-[#9147ff]/15 border border-[#9147ff]/40 text-[#9147ff] font-orbitron text-xs font-bold uppercase tracking-widest hover:bg-[#9147ff]/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-twitch-fill text-base" />
                Follow on Twitch
              </a>
            </div>
          </div>

          {/* Recent VODs */}
          <div
            className={`lg:col-span-3 transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-orbitron text-sm font-bold uppercase tracking-wider text-white">Recent VODs</h3>
              <a href="#" className="font-mono-tech text-xs text-[#22d3ee] hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                View All →
              </a>
            </div>
            <div className="space-y-4">
              {recentVods.map((vod, i) => (
                <div
                  key={vod.id}
                  className={`card-gaming rounded-xl overflow-hidden flex group cursor-pointer transition-all duration-500 ${
                    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{ transitionDelay: `${250 + i * 80}ms` }}
                >
                  {/* Thumbnail */}
                  <div className="relative w-44 flex-shrink-0">
                    <img
                      src={vod.image}
                      alt={vod.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#e879f9]/20 border border-[#e879f9]/60">
                        <i className="ri-play-fill text-[#e879f9] text-lg" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black/70 px-1.5 py-0.5 rounded font-mono-tech text-xs text-white">
                      {vod.duration}
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-4 flex flex-col justify-between flex-1">
                    <h4 className="font-rajdhani text-sm font-semibold text-white leading-snug group-hover:text-[#e879f9] transition-colors duration-200">
                      {vod.title}
                    </h4>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="flex items-center gap-1 font-mono-tech text-xs text-slate-400">
                        <i className="ri-eye-line" />
                        {vod.views}
                      </span>
                      <span className="flex items-center gap-1 font-mono-tech text-xs text-slate-500">
                        <i className="ri-time-line" />
                        {vod.date}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
